import { useMemo, useState } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { reviews, reviewTags } from "@/mocks/reviews";
import { getMovieById } from "@/mocks/movies";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 4;

const ratingOptions = [
  { label: "全部评分", value: 0 },
  { label: "8 分以上", value: 8 },
  { label: "7 分以上", value: 7 },
];

const sortOptions = [
  { label: "最新发布", value: "date" },
  { label: "评分最高", value: "rating" },
  { label: "最热门", value: "views" },
];

export default function ReviewList() {
  useDocumentMeta(
    "影评专区 - 小黄人电影观众真实点评 | 小黄人电影",
    "来自影迷与影评人的小黄人电影影评合集，按评分等级与标签筛选，阅读最真诚的观影感受。"
  );
  const [query, setQuery] = useState("");
  const [tag, setTag] = useState("全部");
  const [minRating, setMinRating] = useState(0);
  const [sort, setSort] = useState("date");
  const [page, setPage] = useState(1);
  const isMobile = useMediaQuery("(max-width: 767px)");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = reviews.filter((r) => {
      if (tag !== "全部" && !r.tags.includes(tag)) return false;
      if (r.rating < minRating) return false;
      if (
        q &&
        !r.title.toLowerCase().includes(q) &&
        !r.summary.toLowerCase().includes(q) &&
        !r.author.toLowerCase().includes(q) &&
        !r.tags.some((t) => t.toLowerCase().includes(q))
      ) {
        return false;
      }
      return true;
    });

    return list.sort((a, b) => {
      if (sort === "rating") return b.rating - a.rating;
      if (sort === "views") return b.views - a.views;
      return b.date.localeCompare(a.date);
    });
  }, [query, tag, minRating, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visible = isMobile
    ? filtered.slice(0, currentPage * PAGE_SIZE)
    : filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const reset = () => {
    setQuery("");
    setTag("全部");
    setMinRating(0);
    setSort("date");
    setPage(1);
  };

  const pill = (active: boolean) =>
    `whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition cursor-pointer ${
      active
        ? "bg-primary-500 text-foreground-950"
        : "border border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
    }`;

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影评专区" }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            影评专区
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            来自影迷与影评人的真实观点。按评分等级或标签筛选，找到最合你口味的那一篇。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1000px] px-4 py-8 md:px-6 md:py-10">
        <div className="rounded-2xl border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex items-center gap-2 rounded-full bg-background-100 px-4 py-2.5">
            <i className="ri-search-line text-foreground-500"></i>
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
              placeholder="搜索影评标题、作者或标签…"
              className="w-full min-w-0 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 outline-none"
            />
            {query && (
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  setPage(1);
                }}
                className="text-foreground-400 hover:text-foreground-700"
                aria-label="清空"
              >
                <i className="ri-close-circle-line"></i>
              </button>
            )}
          </div>

          <div className="mt-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="w-12 shrink-0 text-xs font-medium text-foreground-500">标签</span>
              {["全部", ...reviewTags].map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => {
                    setTag(item);
                    setPage(1);
                  }}
                  className={pill(tag === item)}
                >
                  {item}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="w-12 shrink-0 text-xs font-medium text-foreground-500">评分</span>
              {ratingOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    setMinRating(option.value);
                    setPage(1);
                  }}
                  className={pill(minRating === option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-foreground-500">排序</span>
              {sortOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    setSort(option.value);
                    setPage(1);
                  }}
                  className={pill(sort === option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={reset}
              className="flex items-center gap-1.5 rounded-full border border-background-200 px-4 py-2 text-sm font-medium text-foreground-600 transition hover:border-primary-300 hover:text-primary-700"
            >
              <i className="ri-refresh-line"></i>
              重置
            </button>
          </div>
        </div>

        <p className="mt-6 text-sm text-foreground-500">
          共 <span className="font-semibold text-foreground-900">{filtered.length}</span> 篇影评
        </p>

        {filtered.length === 0 ? (
          <div className="mt-6">
            <EmptyState
              icon="ri-chat-quote-line"
              title="没有符合条件的影评"
              desc="换个标签或评分试试，也可以重置筛选查看全部影评。"
              actionLabel="重置筛选"
              onAction={reset}
            />
          </div>
        ) : (
          <>
            <div className="mt-5 space-y-4">
              {visible.map((review) => {
                const movie = getMovieById(review.movieId);
                return (
                  <ArticleCard
                    key={review.id}
                    to={`/reviews/${review.id}`}
                    title={review.title}
                    cover={movie?.cover}
                    badge={movie?.title}
                    meta={`${review.author} · ${review.date}`}
                    summary={review.summary}
                    tags={review.tags}
                    rating={review.rating}
                    query={query}
                  />
                );
              })}
            </div>
            <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
          </>
        )}
      </div>
    </SiteLayout>
  );
}